'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';

// Backend integration: replace with API call to /api/reports/weekly-breakdown-frequency
const weeklyBreakdownData = [
  { reg: 'GP 23-441', count: 4, lemon: true },
  { reg: 'WC 77-892', count: 3, lemon: true },
  { reg: 'GP 11-203', count: 2, lemon: false },
  { reg: 'NW 44-119', count: 2, lemon: false },
  { reg: 'GP 88-567', count: 1, lemon: false },
  { reg: 'EC 31-774', count: 1, lemon: false },
  { reg: 'GP 55-002', count: 1, lemon: false },
  { reg: 'KZN 19-441', count: 0, lemon: false },
];

interface TooltipPayloadItem {
  value: number;
  payload: { reg: string; count: number; lemon: boolean };
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="card-elevated px-3 py-2.5 shadow-elevated text-xs">
      <p className="font-mono-data font-semibold text-foreground mb-1">{d.reg}</p>
      <p className="text-muted-foreground">
        Breakdowns this week:{' '}
        <span className="text-foreground font-semibold">{d.count}</span>
      </p>
      {d.lemon && (
        <p className="text-warning mt-1 flex items-center gap-1">
          <AlertTriangle size={10} />
          High-failure asset — review recommended
        </p>
      )}
    </div>
  );
};

export default function WeeklyBreakdownChart() {
  return (
    <div className="card-surface p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Breakdown Frequency — This Week
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Per vehicle · 12–18 May 2026 · High-failure assets flagged in red
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm inline-block"
              style={{ backgroundColor: 'var(--danger)' }}
            />
            <span className="text-muted-foreground">High-failure</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm inline-block"
              style={{ backgroundColor: 'var(--primary)' }}
            />
            <span className="text-muted-foreground">Normal</span>
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={weeklyBreakdownData}
          margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            vertical={false}
          />
          <XAxis
            dataKey="reg"
            tick={{
              fill: 'var(--muted-foreground)',
              fontSize: 11,
              fontFamily: 'var(--font-ibm-plex-mono)',
            }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <ReferenceLine
            y={3}
            stroke="var(--warning)"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{
              value: 'Alert threshold',
              fill: 'var(--warning)',
              fontSize: 10,
              position: 'insideTopRight',
            }}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={44}>
            {weeklyBreakdownData.map((entry) => (
              <Cell
                key={`bar-${entry.reg}`}
                fill={entry.lemon ? 'var(--danger)' : 'var(--primary)'}
                fillOpacity={entry.lemon ? 1 : 0.8}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
